/**
 * "Add credential" dialog.
 *
 * Entities come from `GET /api/entities`. The portal is stored as its
 * lookup key (see ./credentialForm), never as a display name.
 */
import { useId, useState } from "react";
import { Loader2, X } from "lucide-react";

import { useEntities } from "@/lib/hooks/useEntities";

import type { CreateCredentialPayload } from "./api";
import {
  OTHER_PORTAL,
  PORTAL_OPTIONS,
  buildCreatePayload,
  normalizePortalKey,
  type CredentialFormValues,
} from "./credentialForm";

interface Props {
  onClose: () => void;
  onSubmit: (p: CreateCredentialPayload) => void;
  submitting: boolean;
  /** Message for a failed save, already made readable by the caller. */
  error: string | null;
}

const INPUT =
  "w-full rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-sm text-ink-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500";

const EMPTY: CredentialFormValues = {
  portalChoice: "",
  customPortal: "",
  entityCode: "",
  username: "",
  password: "",
  mfa: "none",
  totpSecret: "",
  mfaMailbox: "",
};

export function AddCredentialModal({ onClose, onSubmit, submitting, error }: Props) {
  const ids = useId();
  const entitiesQuery = useEntities();
  const entities = entitiesQuery.data?.items ?? [];
  const [v, setV] = useState<CredentialFormValues>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const set = <K extends keyof CredentialFormValues>(key: K, value: CredentialFormValues[K]) =>
    setV((prev) => ({ ...prev, [key]: value }));

  const storedKey =
    v.portalChoice === OTHER_PORTAL ? normalizePortalKey(v.customPortal) : v.portalChoice;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const built = buildCreatePayload(v);
    if (!built.ok) {
      setFormError(built.error);
      return;
    }
    setFormError(null);
    onSubmit(built.payload);
  };

  const field = (name: string) => `${ids}-${name}`;
  const shown = formError ?? error;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-1/30 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={field("title")}
        className="w-full max-w-md rounded-lg border border-paper-line bg-paper-0 p-5 shadow-xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h3 id={field("title")} className="text-sm font-semibold text-ink-1">
            Add credential
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-3 hover:bg-paper-1"
          >
            <X size={14} aria-hidden />
          </button>
        </div>

        <form onSubmit={submit} noValidate className="space-y-3 text-xs text-ink-3">
          <div>
            <label htmlFor={field("portal")}>Portal</label>
            <select
              id={field("portal")}
              value={v.portalChoice}
              onChange={(e) => set("portalChoice", e.target.value)}
              aria-describedby={field("portal-help")}
              className={`mt-1 ${INPUT}`}
            >
              <option value="">Select a portal</option>
              {PORTAL_OPTIONS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
              <option value={OTHER_PORTAL}>Other (enter the portal key)</option>
            </select>
            {v.portalChoice === OTHER_PORTAL && (
              <div className="mt-2">
                <label htmlFor={field("custom")}>Portal key</label>
                <input
                  id={field("custom")}
                  value={v.customPortal}
                  onChange={(e) => set("customPortal", e.target.value)}
                  autoCapitalize="none"
                  spellCheck={false}
                  className={`mt-1 font-mono ${INPUT}`}
                />
              </div>
            )}
            <p id={field("portal-help")} className="mt-1">
              {storedKey ? (
                <>
                  Stored as <code className="font-mono text-ink-1">{storedKey}</code>.{" "}
                </>
              ) : null}
              A workflow finds this login only when its portal key matches exactly.
            </p>
          </div>

          <div>
            <label htmlFor={field("entity")}>Entity</label>
            <select
              id={field("entity")}
              value={v.entityCode}
              onChange={(e) => set("entityCode", e.target.value)}
              disabled={entities.length === 0}
              className={`mt-1 ${INPUT}`}
            >
              <option value="">
                {entitiesQuery.isPending
                  ? "Loading entities…"
                  : entitiesQuery.isError
                    ? "Entities could not be loaded"
                    : "Select an entity"}
              </option>
              {entities.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} · {e.full}
                </option>
              ))}
            </select>
            {entitiesQuery.isError && (
              <button
                type="button"
                onClick={() => entitiesQuery.refetch()}
                className="mt-1 underline"
              >
                Retry
              </button>
            )}
          </div>

          <div>
            <label htmlFor={field("username")}>Username</label>
            <input
              id={field("username")}
              value={v.username}
              onChange={(e) => set("username", e.target.value)}
              autoComplete="off"
              className={`mt-1 ${INPUT}`}
            />
          </div>
          <div>
            <label htmlFor={field("password")}>Password</label>
            <input
              id={field("password")}
              type="password"
              value={v.password}
              onChange={(e) => set("password", e.target.value)}
              autoComplete="new-password"
              className={`mt-1 ${INPUT}`}
            />
          </div>
          <div>
            <label htmlFor={field("mfa")}>MFA type</label>
            <select
              id={field("mfa")}
              value={v.mfa}
              onChange={(e) => set("mfa", e.target.value as CredentialFormValues["mfa"])}
              className={`mt-1 ${INPUT}`}
            >
              <option value="none">None</option>
              <option value="totp">TOTP</option>
              <option value="email">Email</option>
            </select>
          </div>
          {v.mfa === "totp" && (
            <div>
              <label htmlFor={field("totp")}>TOTP seed (base32)</label>
              <input
                id={field("totp")}
                value={v.totpSecret}
                onChange={(e) => set("totpSecret", e.target.value)}
                autoComplete="off"
                className={`mt-1 ${INPUT}`}
              />
            </div>
          )}
          {v.mfa === "email" && (
            <div>
              <label htmlFor={field("mailbox")}>MFA mailbox</label>
              <input
                id={field("mailbox")}
                type="email"
                value={v.mfaMailbox}
                onChange={(e) => set("mfaMailbox", e.target.value)}
                autoComplete="off"
                className={`mt-1 ${INPUT}`}
              />
            </div>
          )}

          {shown && (
            <div role="alert" className="rounded-md bg-status-failed/10 p-2 text-status-failed">
              {shown}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-paper-line bg-paper-0 px-3 py-1.5 text-sm text-ink-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-1 rounded-md bg-ink-1 px-3 py-1.5 text-sm text-paper-0 disabled:opacity-50"
            >
              {submitting && <Loader2 className="animate-spin" size={14} aria-hidden />}
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
